import { Heading, Position } from "@/core/constants/position.ts";
import { FontWeight } from "@/domains/settings/chat/constants/chat";

export enum ChatTab {
  All = "All",
  OOC = "OOC",
  Faction = "Faction",
  Family = "Family",
  News = "News",
  Radio = "Radio",
  System = "System",
}

export enum ChatTarget {
  Native = "Native",
  Error = "Error",
  ValidationError = "ValidationError",
  Speaking = "Speaking",
  SpeakEmote = "SpeakEmote",
  SpeakingOoc = "SpeakingOoc",
  Whisper = "Whisper",
  Shout = "Shout",
  Emote = "Emote",
  Attempt = "Attempt",
  Scene = "Scene",
  News = "News",
  Government = "Government",
  Staff = "Staff",
  World = "World",
  Faction = "Faction",
  FactionOoc = "FactionOoc",
  Family = "Family",
  FamilyOoc = "FamilyOoc",
}

export const chatTargetTabMap: Record<ChatTarget, ChatTab[]> = {
  [ChatTarget.Native]: [ChatTab.All, ChatTab.System],
  [ChatTarget.Error]: [ChatTab.All, ChatTab.System],
  [ChatTarget.ValidationError]: [ChatTab.All, ChatTab.System],
  [ChatTarget.Speaking]: [ChatTab.All, ChatTab.OOC],
  [ChatTarget.SpeakingOoc]: [ChatTab.All, ChatTab.OOC],
  [ChatTarget.Whisper]: [ChatTab.All, ChatTab.OOC],
  [ChatTarget.Shout]: [ChatTab.All, ChatTab.OOC],
  [ChatTarget.Emote]: [ChatTab.All, ChatTab.OOC],
  [ChatTarget.Attempt]: [ChatTab.All, ChatTab.OOC],
  [ChatTarget.Scene]: [ChatTab.All, ChatTab.OOC],
  [ChatTarget.SpeakEmote]: [ChatTab.All, ChatTab.OOC],
  [ChatTarget.News]: [ChatTab.All],
  [ChatTarget.Government]: [ChatTab.All],
  [ChatTarget.Staff]: [ChatTab.All],
  [ChatTarget.World]: [ChatTab.All],
  [ChatTarget.Faction]: [ChatTab.All, ChatTab.Faction],
  [ChatTarget.FactionOoc]: [ChatTab.All, ChatTab.Faction],
  [ChatTarget.Family]: [ChatTab.All, ChatTab.Family],
  [ChatTarget.FamilyOoc]: [ChatTab.All, ChatTab.Family],
};

export enum ChatEntityType {
  Player = "Player",
  Vehicle = "Vehicle",
}

export enum ChatTemplate {
  EngineToggledOn = "EngineToggledOn",
  EngineToggledOff = "EngineToggledOff",
  RandomDice = "RandomDice",
  RandomCoin = "RandomCoin",
  VehicleLocked = "VehicleLocked",
  VehicleUnlocked = "VehicleUnlocked",
}

export type ChatFaction = {
  id: number;
};

export type ChatPlayerTitle = {
  id: number;
  name: string;
};

export type ChatPlayer = {
  playerId: number;
  characterId: number | null;
  name: string | null;
  title: ChatPlayerTitle | null;
};

export type ChatVehicle = {
  vehicleId: number;
  databaseId: number | null;
  name: string | null;
};

export type ChatEntity = {
  type: ChatEntityType;
  content: ChatPlayer | ChatVehicle;
};

export type ChatMessage = {
  target: ChatTarget;
  faction: ChatFaction | null;
  player: ChatPlayer | null;
  entities: ChatEntity[];
  content: string | null;
  template: ChatTemplate | string | null;
  payload: string[] | number[] | boolean | null;
};

export type ChatBrowserPlayer = {
  playerId: number;
  characterId: number | null;
  name: string | null;
  me: boolean;
  know: boolean;
  distance: number | null;
  position: Position | null;
  heading: Heading | null;
  dimension: number | null;
};

export type ChatBrowserMessage = {
  uid: string;
  message: ChatMessage;
  player: ChatBrowserPlayer | null;
  ooc: boolean; // out of character = /b = (( text ))
  date: Date;
};

export const COMMAND_REG_EXP = /^\/\S*\s*/;

export const MAX_MESSAGES = 100;

export const fontWeightClass: Record<FontWeight, string> = {
  [FontWeight.Thin]: "font-thin",
  [FontWeight.Extralight]: "font-extralight",
  [FontWeight.Light]: "font-light",
  [FontWeight.Normal]: "font-normal",
  [FontWeight.Medium]: "font-medium",
  [FontWeight.Semibold]: "font-semibold",
  [FontWeight.Bold]: "font-bold",
  [FontWeight.Extrabold]: "font-extrabold",
  [FontWeight.Black]: "font-black",
};

export const fontSizeClass = {
  12: "text-[12px]",
  14: "text-[14px]",
  16: "text-[16px]",
  18: "text-[18px]",
  20: "text-[20px]",
  22: "text-[22px]",
  24: "text-[24px]",
};

export const backgroundOpacityClass = {
  0: "bg-gradient-to-t from-[#130F14] to-[#130F14]",
  5: "bg-gradient-to-t from-[#130F14] to-[#130F14]/95",
  10: "bg-gradient-to-t from-[#130F14]/95 to-[#130F14]/90",
  15: "bg-gradient-to-t from-[#130F14]/90 to-[#130F14]/85",
  20: "bg-gradient-to-t from-[#130F14]/85 to-[#130F14]/80",
  25: "bg-gradient-to-t from-[#130F14]/80 to-[#130F14]/75",
  30: "bg-gradient-to-t from-[#130F14]/75 to-[#130F14]/70",
  35: "bg-gradient-to-t from-[#130F14]/70 to-[#130F14]/65",
  40: "bg-gradient-to-t from-[#130F14]/65 to-[#130F14]/60",
  45: "bg-gradient-to-t from-[#130F14]/60 to-[#130F14]/55",
  50: "bg-gradient-to-t from-[#130F14]/55 to-[#130F14]/50",
  55: "bg-gradient-to-t from-[#130F14]/50 to-[#130F14]/45",
  60: "bg-gradient-to-t from-[#130F14]/45 to-[#130F14]/40",
  65: "bg-gradient-to-t from-[#130F14]/40 to-[#130F14]/35",
  70: "bg-gradient-to-t from-[#130F14]/35 to-[#130F14]/30",
  75: "bg-gradient-to-t from-[#130F14]/30 to-[#130F14]/25",
  80: "bg-gradient-to-t from-[#130F14]/25 to-[#130F14]/20",
  85: "bg-gradient-to-t from-[#130F14]/20 to-[#130F14]/15",
  90: "bg-gradient-to-t from-[#130F14]/15 to-[#130F14]/10",
  95: "bg-gradient-to-t from-[#130F14]/10 to-[#130F14]/5",
  100: "bg-gradient-to-t from-[#130F14]/5 to-[#130F14]/0",
};

export const messageOpacityClass = {
  0: "opacity-100",
  5: "opacity-95",
  10: "opacity-90",
  15: "opacity-85",
  20: "opacity-80",
  25: "opacity-75",
  30: "opacity-70",
  35: "opacity-65",
  40: "opacity-60",
  45: "opacity-55",
  50: "opacity-50",
  55: "opacity-45",
  60: "opacity-40",
  65: "opacity-35",
  70: "opacity-30",
  75: "opacity-25",
  80: "opacity-20",
  85: "opacity-15",
  90: "opacity-10",
  95: "opacity-5",
  100: "opacity-0",
};
